import React, { useEffect, useRef, useState } from "react";
import { Radio, ExternalLink } from "lucide-react";

/**
 * Radio button + station-picker popover.
 *
 * Placed above the Nomad Life logo in the member sidebar. Clicking the
 * button toggles a compact popover with two curated internet-radio stations.
 * Both SomaFM and AccuRadio ship X-Frame-Options headers that block iframe
 * embedding, so each option opens the station's own player in a new tab
 * (with rel="noopener" for safety).
 */
const STATIONS = [
  {
    key: "somafm",
    name: "SomaFM",
    tagline: "Listener-supported, commercial-free internet radio from San Francisco.",
    href: "https://somafm.com/player/",
    accent: "#c89b7c",
  },
  {
    key: "accuradio",
    name: "AccuRadio",
    tagline: "Hundreds of curated channels across genres — pick a mood and go.",
    href: "https://www.accuradio.com/",
    accent: "#7da27a",
  },
];

export default function RadioMenu() {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  // Close on outside click / Escape
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div className="relative" ref={wrapRef} data-testid="radio-menu-wrap">
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        aria-expanded={open}
        aria-haspopup="menu"
        className="w-full flex items-center gap-2 px-3 py-2 text-sm tracking-wide border border-[var(--hh-border)] text-[var(--hh-cream)] hover:border-[var(--hh-accent)] hover:text-[var(--hh-accent)] transition-colors"
        data-testid="radio-menu-toggle"
      >
        <Radio size={16} />
        <span>Radio</span>
      </button>

      {open && (
        <div
          role="menu"
          className="absolute left-0 right-0 mt-2 z-40 fade-in"
          style={{
            background: "rgba(14, 27, 20, 0.98)",
            border: "1px solid var(--hh-border)",
            backdropFilter: "blur(8px)",
            boxShadow: "0 12px 32px rgba(0,0,0,0.35)",
          }}
          data-testid="radio-menu-popover"
        >
          <div className="px-4 py-3 border-b border-[var(--hh-border)]">
            <span className="label-eyebrow text-[var(--hh-accent)]">Tune in</span>
            <p className="text-xs text-[var(--hh-sage)] mt-1 leading-snug">
              Streaming radio to soundtrack your workday. Opens in a new tab.
            </p>
          </div>
          <div className="p-2 space-y-1">
            {STATIONS.map(s => (
              <a
                key={s.key}
                href={s.href}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setOpen(false)}
                className="flex items-start gap-3 px-3 py-3 text-left border border-transparent hover:border-[var(--hh-sage)] hover:bg-[var(--hh-input)] transition-colors group"
                data-testid={`radio-station-${s.key}`}
              >
                <div
                  className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
                  style={{ background: `${s.accent}22`, border: `1px solid ${s.accent}` }}
                >
                  <Radio size={14} color={s.accent} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-display text-lg text-[var(--hh-cream)] group-hover:text-[var(--hh-accent)] transition-colors">
                      {s.name}
                    </span>
                    <ExternalLink size={10} className="text-[var(--hh-sage)] group-hover:text-[var(--hh-accent)] transition-colors" />
                  </div>
                  <p className="text-xs text-[var(--hh-sage)] mt-0.5 leading-snug">
                    {s.tagline}
                  </p>
                </div>
              </a>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
